import chalk from 'chalk';
import { randomBytes } from 'crypto';
import fs from 'fs-extra';
import { homedir } from 'os';
import { basename, dirname, isAbsolute, join, relative, resolve } from 'path';
import { logger } from './logger.js';
import {
  DOCKER_ENV_REL_PATH,
  ENV_DP_PASSWORD,
  ENV_DP_PFX,
  ENV_DP_PREVIOUS_PASSWORD,
  ENV_DP_PREVIOUS_PFX,
  ENV_JWT_SECRET,
  mergeDotEnv,
  parseDotEnv,
  setCertificateInfoComment,
} from './docker-env.js';
import {
  generateDataProtectionCertificate,
  type DataProtectionCertificate,
  type GenerateCertificateOptions,
} from './dataprotection-cert.js';
import { findDockerComposeFiles, findKeyRingOptOuts } from './dataprotection-diagnostics.js';

// ============================================================================
// DataProtection certificate — provisioning
//
// Two destinations, never the repository:
//   - the docker stack: docker-images/.env (gitignored), read by compose and
//     mapped by docker-compose.yml onto DataProtection__Certificate__Pfx/Password;
//   - a deployment: a PFX + password pair written OUTSIDE any git work tree
//     (default ~/.smartstack/secrets/<project>/), to escrow in a vault and inject
//     as environment variables.
//
// Key material is never destroyed: replacing the docker certificate parks the
// previous one in DATAPROTECTION_PREVIOUS_*, and a deployment rotation writes a
// new dated pair beside the old one. Dev machines (Development/Local) are exempt
// on the platform side and get nothing.
// ============================================================================

export type CertificateGenerator = (opts: GenerateCertificateOptions) => DataProtectionCertificate;

/** Comment block written above the secrets the first time they are appended. */
export const DOCKER_SECRETS_HEADER: readonly string[] = [
  '# SmartStack docker stack secrets — read by docker compose for docker-images/docker-compose.yml.',
  '# NEVER commit this file (gitignored). Keep a copy of the DataProtection certificate (PFX + password)',
  '# in a vault: losing it makes every secret the platform encrypted unreadable (TOTP, API keys,',
  '# client secrets, license machine secret). Regenerate with `ss secrets dataprotection --docker --rotate`.',
];

const PROVISION_HINT = 'Run "ss secrets dataprotection --docker" to provision';

/**
 * The backend `environment:` lines of templates/project/docker-compose.yml.template
 * (a test pins them to it), printed for a compose file the client edited — the
 * docker sync never rewrites one. `:?` makes compose stop with the fix instead of
 * starting a backend that refuses to boot.
 */
export const CERTIFICATE_COMPOSE_LINES: readonly string[] = [
  `- 'DataProtection__Certificate__Pfx=\${${ENV_DP_PFX}:?${PROVISION_HINT} the DataProtection certificate}'`,
  `- 'DataProtection__Certificate__Password=\${${ENV_DP_PASSWORD}:?${PROVISION_HINT} the DataProtection certificate}'`,
  `- 'Jwt__Secret=\${${ENV_JWT_SECRET}:?${PROVISION_HINT} the JWT signing secret}'`,
];

/**
 * The PERMANENT previous-certificate slot of the compose template (its
 * `AdditionalCertificateSlot` block — a test pins them to it). Empty, the
 * platform ignores the entry from SmartStack 3.70, so a rotation only fills
 * DATAPROTECTION_PREVIOUS_*. Rendered, and suggested, for ≥ 3.70 only.
 */
export const ADDITIONAL_CERTIFICATE_SLOT_LINES: readonly string[] = [
  `- 'DataProtection__AdditionalCertificates__0__Pfx=\${${ENV_DP_PREVIOUS_PFX}:-}'`,
  `- 'DataProtection__AdditionalCertificates__0__Password=\${${ENV_DP_PREVIOUS_PASSWORD}:-}'`,
];

/**
 * Before SmartStack 3.70 an EMPTY AdditionalCertificates entry fails startup
 * ("AdditionalCertificates:0:Pfx is required"): the mapping may only exist while
 * a previous certificate does, hence `:?` and no slot in the template.
 */
export const PREVIOUS_CERTIFICATE_COMPOSE_LINES: readonly string[] = [
  `- 'DataProtection__AdditionalCertificates__0__Pfx=\${${ENV_DP_PREVIOUS_PFX}:?the previous DataProtection certificate is missing from docker-images/.env}'`,
  `- 'DataProtection__AdditionalCertificates__0__Password=\${${ENV_DP_PREVIOUS_PASSWORD}:?the previous DataProtection certificate is missing from docker-images/.env}'`,
];

/** The previous-certificate lines a compose file should carry for this platform version. */
export function previousCertificateComposeLines(slotSupported: boolean): readonly string[] {
  return slotSupported ? ADDITIONAL_CERTIFICATE_SLOT_LINES : PREVIOUS_CERTIFICATE_COMPOSE_LINES;
}

/** Patterns that keep docker-images/.env out of git. */
const ENV_IGNORE_PATTERNS = new Set([
  '*.env', '.env', '**/.env', '.env*', 'docker-images/.env', '/docker-images/.env',
  'docker-images/*.env', '/docker-images/*.env', 'docker-images/', '/docker-images/',
]);

export interface PublicCertificateInfo {
  subject: string;
  thumbprint: string;
  notAfter: string;
}

export interface DockerProvisionOptions {
  projectDir: string;
  projectName: string;
  /** Replace an existing certificate (the previous one is parked, never lost). */
  replaceCertificate?: boolean;
  dryRun?: boolean;
  generate?: CertificateGenerator;
}

export interface DockerProvisionResult {
  envPath: string;
  envCreated: boolean;
  /** Keys written (or that would be, in dry-run). */
  written: string[];
  /** Keys left untouched because they already held a value. */
  kept: string[];
  /** Set when a certificate was generated. */
  certificate: PublicCertificateInfo | null;
  /** The previous certificate was moved to DATAPROTECTION_PREVIOUS_*. */
  previousParked: boolean;
  /** .gitignore gained a docker-images/.env line. */
  gitignoreUpdated: boolean;
}

const isSet = (values: Map<string, string>, key: string): boolean => (values.get(key) ?? '').trim().length > 0;

/**
 * Fill docker-images/.env with the docker stack's secrets. Idempotent: run it
 * twice and the second run writes nothing.
 */
export async function provisionDockerSecrets(opts: DockerProvisionOptions): Promise<DockerProvisionResult> {
  const envPath = join(opts.projectDir, ...DOCKER_ENV_REL_PATH.split('/'));
  const envExists = await fs.pathExists(envPath);
  const original = envExists ? await fs.readFile(envPath, 'utf-8') : '';
  const values = parseDotEnv(original);

  const hasPfx = isSet(values, ENV_DP_PFX);
  const hasPassword = isSet(values, ENV_DP_PASSWORD);
  if (hasPfx !== hasPassword && !opts.replaceCertificate) {
    // Filling only the blank half would pair a certificate with someone else's password.
    throw new Error(
      `${DOCKER_ENV_REL_PATH}: ${hasPfx ? ENV_DP_PFX : ENV_DP_PASSWORD} is set but ${hasPfx ? ENV_DP_PASSWORD : ENV_DP_PFX} is empty — ` +
      'restore the missing half from your vault, or replace the certificate with --rotate.',
    );
  }

  const toWrite: Array<[string, string]> = [];
  const kept: string[] = [];
  let previousParked = false;
  let certificate: DataProtectionCertificate | null = null;

  const needCertificate = opts.replaceCertificate || !hasPfx;
  if (needCertificate) {
    // Park whatever exists, even half a pair: it may be the only copy of that PFX.
    if (hasPfx || hasPassword) {
      if (isSet(values, ENV_DP_PREVIOUS_PFX)) {
        throw new Error(
          `${DOCKER_ENV_REL_PATH} already parks a previous certificate (${ENV_DP_PREVIOUS_PFX}) — a second rotation would ` +
          'push it out of the stack. Keep it in a vault and clear it once the platform no longer needs it, then rotate again.',
        );
      }
      toWrite.push([ENV_DP_PREVIOUS_PFX, values.get(ENV_DP_PFX) ?? ''], [ENV_DP_PREVIOUS_PASSWORD, values.get(ENV_DP_PASSWORD) ?? '']);
      previousParked = true;
    }
    if (!opts.dryRun) {
      certificate = (opts.generate ?? generateDataProtectionCertificate)({
        projectName: opts.projectName,
        environment: 'docker',
      });
      toWrite.push([ENV_DP_PFX, certificate.pfxBase64], [ENV_DP_PASSWORD, certificate.password]);
    } else {
      toWrite.push([ENV_DP_PFX, ''], [ENV_DP_PASSWORD, '']);
    }
  } else {
    kept.push(ENV_DP_PFX, ENV_DP_PASSWORD);
  }

  if (isSet(values, ENV_JWT_SECRET)) {
    kept.push(ENV_JWT_SECRET);
  } else {
    toWrite.push([ENV_JWT_SECRET, randomBytes(64).toString('base64')]);
  }

  const written = toWrite.map(([key]) => key);
  let gitignoreUpdated = false;

  if (!opts.dryRun && toWrite.length > 0) {
    let content = mergeDotEnv(original, toWrite, { force: true, header: DOCKER_SECRETS_HEADER }).content;
    if (certificate) content = setCertificateInfoComment(content, certificate);
    await fs.ensureDir(dirname(envPath));
    await fs.writeFile(envPath, content, { encoding: 'utf-8', mode: 0o600 });
  }
  if (!opts.dryRun) {
    gitignoreUpdated = await ensureDockerEnvIgnored(opts.projectDir);
  }

  return {
    envPath,
    envCreated: !envExists && written.length > 0,
    written,
    kept,
    certificate: certificate
      ? { subject: certificate.subject, thumbprint: certificate.thumbprint, notAfter: certificate.notAfter }
      : null,
    previousParked,
    gitignoreUpdated,
  };
}

export interface DockerBackfillResult {
  /** Keys back-filled into docker-images/.env. */
  dockerSecretsProvisioned: string[];
  /** The compose file maps no certificate (edited by hand, so the sync left it). */
  dockerComposeLacksCertificate: boolean;
  /** `file` or `file:line` of every RequireEncryptedKeyRing=false. */
  keyRingOptOuts: string[];
}

/**
 * `ss upgrade` back-fill for an existing project. The compose template change
 * reaches an UNMODIFIED compose file through the docker sync; this provisions
 * the secrets it now requires (never overwriting a value), tells the owner of an
 * edited compose file which lines to add, and reports — without editing — every
 * RequireEncryptedKeyRing=false opt-out. A generation failure is reported, not
 * thrown: the rest of the upgrade must go on.
 */
export async function backfillDockerDataProtection(opts: {
  projectDir: string;
  projectName: string;
  dryRun?: boolean;
  /** Dry-run only: the docker sync WOULD rewrite the compose file, so its disk content is stale. */
  composeUpdatePending?: boolean;
  /** The project runs SmartStack ≥ 3.70 (empty AdditionalCertificates slot allowed). */
  slotSupported?: boolean;
  generate?: CertificateGenerator;
}): Promise<DockerBackfillResult> {
  let dockerSecretsProvisioned: string[] = [];
  let dockerComposeLacksCertificate = false;

  const composeFiles = await findDockerComposeFiles(opts.projectDir);
  if (composeFiles.length > 0) {
    try {
      const provision = await provisionDockerSecrets({
        projectDir: opts.projectDir, projectName: opts.projectName, dryRun: opts.dryRun, generate: opts.generate,
      });
      logDockerProvisionResult(provision, opts.dryRun);
      dockerSecretsProvisioned = provision.written;
    } catch (error) {
      logger.warning(`Could not provision ${DOCKER_ENV_REL_PATH}: ${error instanceof Error ? error.message : error}`);
      logger.warning(`Run ${chalk.cyan('ss secrets dataprotection --docker')} before the next docker run.`);
    }

    const compose = (await Promise.all(composeFiles.map((f) => fs.readFile(f, 'utf-8'))))
      .join('\n')
      .split(/\r?\n/)
      .filter((line) => !/^\s*#/.test(line))
      .join('\n');
    if (!opts.composeUpdatePending && !compose.includes('DataProtection__Certificate__Pfx')) {
      dockerComposeLacksCertificate = true;
      logger.warning('docker-images/docker-compose.yml maps no DataProtection certificate (modified locally, so not updated).');
      logger.warning('Outside Development/Local the backend refuses to start. Add to the backend `environment:`:');
      const lines = opts.slotSupported ? [...CERTIFICATE_COMPOSE_LINES, ...ADDITIONAL_CERTIFICATE_SLOT_LINES] : CERTIFICATE_COMPOSE_LINES;
      for (const line of lines) console.log(chalk.cyan(`      ${line}`));
    }
  }

  const keyRingOptOuts = (await findKeyRingOptOuts(opts.projectDir)).map((o) => (o.line ? `${o.file}:${o.line}` : o.file));
  if (keyRingOptOuts.length > 0) {
    logger.error('DataProtection:RequireEncryptedKeyRing=false is set — the platform refuses this opt-out outside Development/Local:');
    for (const where of keyRingOptOuts) console.log(chalk.red(`    - ${where}`));
    console.log(chalk.yellow(`  Remove it (ss upgrade does not edit these files), then provide a certificate: ${chalk.cyan('ss secrets dataprotection --docker')}`));
    console.log(chalk.yellow(`  for the docker stack, ${chalk.cyan('ss secrets dataprotection --env production')} for a deployment.`));
  }

  return { dockerSecretsProvisioned, dockerComposeLacksCertificate, keyRingOptOuts };
}

/** True when a .gitignore line keeps docker-images/.env out of git. */
export function gitignoreCoversDockerEnv(gitignore: string): boolean {
  return gitignore
    .split(/\r?\n/)
    .map((l) => l.trim())
    .some((l) => ENV_IGNORE_PATTERNS.has(l));
}

/** Append `docker-images/.env` to the project's .gitignore unless already covered. */
export async function ensureDockerEnvIgnored(projectDir: string): Promise<boolean> {
  const path = join(projectDir, '.gitignore');
  const existing = (await fs.pathExists(path)) ? await fs.readFile(path, 'utf-8') : '';
  if (gitignoreCoversDockerEnv(existing)) return false;
  const sep = existing.length === 0 || existing.endsWith('\n') ? '' : '\n';
  await fs.writeFile(
    path,
    `${existing}${sep}\n# SmartStack — docker stack secrets (DataProtection certificate, JWT secret): never commit\n${DOCKER_ENV_REL_PATH}\n`,
    'utf-8',
  );
  return true;
}

/** Walk up from `dir` (or its first existing ancestor) looking for a .git entry. */
export function findEnclosingGitWorkTree(dir: string): string | null {
  let current = resolve(dir);
  for (;;) {
    if (fs.existsSync(join(current, '.git'))) return current;
    const parent = dirname(current);
    if (parent === current) return null;
    current = parent;
  }
}

export function defaultSecretsDir(projectName: string): string {
  return join(homedir(), '.smartstack', 'secrets', projectName);
}

/** Keep environment names file-system friendly. */
export function normalizeEnvironmentName(environment: string): string {
  const normalized = environment.trim().toLowerCase();
  if (!/^[a-z0-9][a-z0-9_-]{0,39}$/.test(normalized)) {
    throw new Error(`invalid environment name "${environment}" (letters, digits, - and _ only)`);
  }
  return normalized;
}

export interface EscrowOptions {
  projectName: string;
  environment: string;
  outDir: string;
  /** The project's folder: `outDir` must not lie inside its git work tree. */
  projectDir: string;
  /** Write a new, dated pair beside an existing one instead of refusing. */
  rotate?: boolean;
  now?: Date;
  generate?: CertificateGenerator;
}

export interface EscrowResult {
  pfxPath: string;
  passwordPath: string;
  certificate: PublicCertificateInfo;
  /** The pair this one succeeds, when rotating. */
  previousPfxPath: string | null;
}

const isInside = (child: string, parent: string): boolean => {
  const rel = relative(parent, child);
  return rel === '' || (!rel.startsWith('..') && !isAbsolute(rel));
};

/**
 * Write a deployment certificate as a PFX + password pair to `outDir`. Refused
 * inside the project's git work tree (a PFX next to the code ends up committed,
 * zipped or in a build context); anywhere else the folder gets a `.gitignore`
 * holding `*`, so even an enclosing repository (a versioned home directory)
 * never picks the files up. Existing files are never overwritten.
 */
export async function writeEscrowCertificate(opts: EscrowOptions): Promise<EscrowResult> {
  const environment = normalizeEnvironmentName(opts.environment);
  const outDir = resolve(opts.outDir);

  const projectTree = findEnclosingGitWorkTree(opts.projectDir);
  if (projectTree && isInside(outDir, projectTree)) {
    throw new Error(
      `refusing to write a certificate inside the project's git work tree ${projectTree} — a PFX next to the code ends up ` +
      'committed. Pick a folder outside the repository (--out); the default is ~/.smartstack/secrets/<project>/.',
    );
  }

  const base = `dataprotection-${environment}`;
  const basePfx = join(outDir, `${base}.pfx`);
  let pfxPath = basePfx;
  let passwordPath = join(outDir, `${base}.password.txt`);
  let previousPfxPath: string | null = null;

  if (await fs.pathExists(basePfx)) {
    if (!opts.rotate) {
      throw new Error(
        `${basePfx} already exists — it may be the only copy of the certificate protecting ${environment}. ` +
        'Use --rotate to create a successor beside it (the existing pair is never overwritten).',
      );
    }
    const stamp = (opts.now ?? new Date()).toISOString().replace(/[-:]/g, '').replace(/\..*$/, '').replace('T', '-');
    pfxPath = join(outDir, `${base}-${stamp}.pfx`);
    passwordPath = join(outDir, `${base}-${stamp}.password.txt`);
    previousPfxPath = basePfx;
    if (await fs.pathExists(pfxPath)) throw new Error(`${pfxPath} already exists`);
  }

  const certificate = (opts.generate ?? generateDataProtectionCertificate)({
    projectName: opts.projectName,
    environment,
  });

  await fs.ensureDir(outDir);
  const selfIgnore = join(outDir, '.gitignore');
  if (!(await fs.pathExists(selfIgnore))) {
    await fs.writeFile(selfIgnore, '# SmartStack secrets — never versioned, whatever repository encloses this folder\n*\n', 'utf-8');
  }
  // 'wx': fail rather than overwrite, even in a race.
  await fs.writeFile(pfxPath, Buffer.from(certificate.pfxBase64, 'base64'), { mode: 0o600, flag: 'wx' });
  await fs.writeFile(passwordPath, certificate.password, { encoding: 'utf-8', mode: 0o600, flag: 'wx' });

  return {
    pfxPath,
    passwordPath,
    certificate: { subject: certificate.subject, thumbprint: certificate.thumbprint, notAfter: certificate.notAfter },
    previousPfxPath,
  };
}

/** Project name for the certificate subject: .smartstack/config.json, else the folder name. */
export async function resolveProjectName(projectDir: string): Promise<string> {
  try {
    const config = await fs.readJson(join(projectDir, '.smartstack', 'config.json')) as { baseNamespace?: string };
    if (config.baseNamespace) return config.baseNamespace;
  } catch { /* no config: fall back to the folder name */ }
  return basename(resolve(projectDir));
}

export function logCertificateInfo(info: PublicCertificateInfo): void {
  console.log(`    Subject:    ${chalk.cyan(info.subject)}`);
  console.log(`    Thumbprint: ${chalk.cyan(info.thumbprint)}`);
  console.log(`    Expires:    ${chalk.cyan(info.notAfter.slice(0, 10))}`);
}

/** Console report of a docker provisioning — key NAMES only, never values. */
export function logDockerProvisionResult(result: DockerProvisionResult, dryRun = false): void {
  const prefix = dryRun ? '[DRY RUN] ' : '';
  if (result.written.length === 0) {
    logger.info(`${prefix}${DOCKER_ENV_REL_PATH} ${chalk.green('✓')} already provisioned (${result.kept.join(', ')})`);
    return;
  }
  const verb = dryRun ? 'Would write' : result.envCreated ? 'Created' : 'Updated';
  logger.success(`${prefix}${verb} ${DOCKER_ENV_REL_PATH}: ${result.written.join(', ')}`);
  if (result.kept.length > 0) logger.info(`  kept as is: ${result.kept.join(', ')}`);
  if (result.certificate) logCertificateInfo(result.certificate);
  if (result.gitignoreUpdated) logger.info(`  .gitignore now excludes ${DOCKER_ENV_REL_PATH}`);
}

/** The escrow + injection + rotation guidance, shared by `ss secrets` and `ss init`. */
export function escrowGuidance(): string[] {
  return [
    'Store the PFX AND its password in a vault (Key Vault, 1Password, …) — OUT of the repository and OUT of the',
    'database backups: a dump carries the encrypted secrets, the certificate must not travel with it.',
    'Inject it in the target environment as:',
    '  DataProtection__Certificate__Pfx       = <base64 of the PFX>  (or a path to the PFX file)',
    '  DataProtection__Certificate__Password  = <its password>',
    'Rotation: the NEW certificate goes to DataProtection:Certificate, the old one moves to',
    '  DataProtection:AdditionalCertificates:0 (Pfx + Password) and stays there — it still decrypts the existing keys.',
    'Losing it loses every secret the platform encrypted: TOTP enrolments, API keys, connector and client secrets,',
    '  the license machine secret. Development/Local machines are exempt and need none.',
  ];
}
